/**
 * Silent re-authentication.
 *
 * Treats the persisted Firebase session as the source of truth and the backend
 * JWT as a short-lived, silently-renewable derivative. The Firebase session
 * (long-lived refresh token, auto-refreshing ID token) is exchanged for a fresh
 * backend JWT without any popup, so an in-session token expiry does not bounce
 * the user to `/login`.
 *
 * Silent re-auth is strictly a session KEEP-ALIVE, never a login: it only runs
 * while a backend session exists in localStorage, and only when the persisted
 * Firebase identity matches that session's user. It therefore cannot mint a
 * session from a cold logged-out state (e.g. a stale Firebase session left by
 * an old logout on a shared device) and cannot swap an email/password user's
 * session for a lingering provider identity.
 *
 * Two entry points use the same exchange:
 *  - `useFirebaseSilentAuth` — proactively re-mints on auth-state restoration
 *    and on an interval as the token nears expiry.
 *  - the shared axios 401 interceptor (attachSilentAuthRetry) — reactively
 *    re-mints once before falling back to clear-session-and-redirect.
 */

import { Capacitor } from "@capacitor/core";
import {
  accessTokenNeedsRefresh,
  getStoredSessionUser,
  sessionSlice,
  wasIntentionalLogout,
} from "@store/session";
import { store } from "@store/store";
import type { AuthResponse } from "@types";
import { getApiUrl, safeConsole } from "@utils";
import type { InternalAxiosRequestConfig } from "axios";
import axios, { type AxiosInstance, type AxiosResponse } from "axios";
import { type User as FirebaseUser, onAuthStateChanged } from "firebase/auth";
import { useEffect } from "react";
import {
  reportTerminalAuthFailure,
  type TerminalAuthRecoveryOutcome,
} from "@/services/apiErrorReporting";
import { attachAuthOriginGuard } from "@/services/authOriginGuard";
import { getFirebaseAuth, isFirebaseReady } from "../../firebase";
import { isImpersonationAuthorization } from "./impersonationToken";

declare module "axios" {
  // Marks a request already retried once after a silent re-auth, so a retry that
  // still 401s doesn't loop.
  interface InternalAxiosRequestConfig {
    _retry?: boolean;
  }
}

// How often to proactively check whether the backend JWT is near expiry while
// the tab stays open. Must be shorter than the session's TOKEN_REFRESH_SKEW so a
// check always runs while the token is still valid (covers tabs left open past
// the 1-day TTL).
const REFRESH_CHECK_INTERVAL_MS = 5 * 60 * 1000;

// Cap how long we wait for Firebase's auth-state observer to fire. Firebase
// normally fires it promptly, but a broken state (corrupted IndexedDB, service
// worker interference) could stall it — and since silentReauth is awaited by the
// axios 401 interceptor, an unbounded wait would hang every in-flight request.
// On timeout we resolve null so the interceptor falls through to its redirect.
const AUTH_STATE_TIMEOUT_MS = 5000;

// After a failed exchange, back off before the NEXT proactive attempt so a
// persistent backend failure isn't hammered by the interval (linear per-failure
// growth, capped). The reactive 401 path is not backed off — it is already
// bounded to one attempt per request by the `_retry` flag.
const BACKOFF_STEP_MS = 5 * 60 * 1000;
const BACKOFF_MAX_MS = 30 * 60 * 1000;

/** Proactive-retry backoff after `failures` consecutive failed exchanges. */
export const proactiveBackoffMs = (failures: number): number =>
  Math.min(failures * BACKOFF_STEP_MS, BACKOFF_MAX_MS);

// Isolated axios instance for the token exchange. The global axios carries the
// 401 redirect interceptor (attachSilentAuthRetry); a transient 401 from
// /auth/firebase-login would otherwise trigger its checkAuth + window.location
// redirect and bounce a user who still holds a valid backend JWT during a
// proactive refresh. The origin guard is defense-in-depth (this call sends no
// Authorization header); the ngrok header mirrors createAuthenticatedClient.
const authExchangeClient = axios.create({
  headers: { "ngrok-skip-browser-warning": "true" },
});
attachAuthOriginGuard(authExchangeClient);

export type SilentReauthUser = Pick<FirebaseUser, "email" | "getIdToken">;

export interface SilentReauthDependencies {
  dispatchTokenRefreshed: (response: AuthResponse) => void;
  exchangeIdToken: (idToken: string) => Promise<AuthResponse>;
  getCurrentFirebaseUser: () => Promise<SilentReauthUser | null>;
  getStoredSessionUser: typeof getStoredSessionUser;
  isFirebaseReady: typeof isFirebaseReady;
  logger: Pick<typeof safeConsole, "error" | "warn">;
  wasIntentionalLogout: typeof wasIntentionalLogout;
}

/**
 * Resolve the current Firebase user, waiting once for auth-state restoration if
 * it hasn't completed yet (e.g. a 401 fires during a cold start before Firebase
 * has rehydrated its persisted session). Resolves null if restoration doesn't
 * settle within AUTH_STATE_TIMEOUT_MS, so callers never await indefinitely.
 */
export const waitForCurrentFirebaseUser = (
  currentUser: FirebaseUser | null,
  subscribe: (next: (user: FirebaseUser | null) => void) => () => void,
): Promise<FirebaseUser | null> =>
  new Promise((resolve) => {
    if (currentUser) {
      resolve(currentUser);
      return;
    }
    let settled = false;
    let unsubscribe: (() => void) | undefined;
    const finish = (user: FirebaseUser | null) => {
      if (settled) {
        return;
      }
      settled = true;
      clearTimeout(timer);
      unsubscribe?.();
      resolve(user);
    };
    const timer = setTimeout(() => finish(null), AUTH_STATE_TIMEOUT_MS);
    unsubscribe = subscribe(finish);
  });

export const createSilentReauth = ({
  dispatchTokenRefreshed,
  exchangeIdToken,
  getCurrentFirebaseUser,
  getStoredSessionUser: readStoredSessionUser,
  isFirebaseReady: firebaseIsReady,
  logger,
  wasIntentionalLogout: didIntentionallyLogout,
}: SilentReauthDependencies): (() => Promise<boolean>) => {
  let inFlightReauth: Promise<boolean> | null = null;

  const runSilentReauth = async (): Promise<boolean> => {
    if (!firebaseIsReady()) {
      return false;
    }
    // Respect a deliberate logout even if Firebase's signOut failed to clear its
    // persisted session — don't silently sign the user back in.
    if (didIntentionallyLogout()) {
      return false;
    }
    // Keep-alive only: without an existing backend session there is nothing to
    // extend. A lingering Firebase session with no app session (e.g. left behind
    // by a logout before Firebase sign-out shipped) must not become a login.
    const sessionUser = readStoredSessionUser();
    if (!sessionUser?.email) {
      return false;
    }
    const user = await getCurrentFirebaseUser();
    if (!user) {
      return false;
    }
    // Identity gate: only re-mint for the SAME account that owns the current
    // session. On a shared browser, an email/password (or different provider)
    // session must never be silently swapped for whatever Firebase identity
    // happens to persist in IndexedDB.
    if (user.email?.toLowerCase() !== sessionUser.email.toLowerCase()) {
      logger.warn(
        "authentication-lifecycle",
        "Silent re-auth skipped: Firebase identity does not match the session user.",
      );
      return false;
    }

    try {
      const idToken = await user.getIdToken(/* forceRefresh */ true);
      const data = await exchangeIdToken(idToken);
      // Re-check after the await: a logout may have raced this in-flight
      // exchange; applying the token now would resurrect the ended session.
      if (didIntentionallyLogout()) {
        return false;
      }
      // tokenRefreshed (not `login`): preserves the currently selected company
      // and does not clear the intentional-logout marker.
      dispatchTokenRefreshed(data);
      return true;
    } catch (error) {
      // The shared boundary redacts the posted Firebase ID token while retaining
      // the remaining request/response evidence.
      logger.error("authentication-lifecycle", "Silent re-auth failed.", error);
      return false;
    }
  };

  return () => {
    if (inFlightReauth) {
      return inFlightReauth;
    }
    inFlightReauth = runSilentReauth()
      .catch(() => false)
      .finally(() => {
        inFlightReauth = null;
      });
    return inFlightReauth;
  };
};

/**
 * Silently exchange the persisted Firebase session for a fresh backend JWT, with
 * no popup. Resolves true when a new token was stored, false when there is no
 * matching Firebase session to use or the exchange failed. Never rejects, so an
 * awaiting 401 interceptor always reaches its redirect fallback.
 *
 * Single-flight: concurrent callers (e.g. several requests 401-ing at once)
 * share one in-flight exchange instead of stampeding `/auth/firebase-login`.
 */
const silentReauth = createSilentReauth({
  dispatchTokenRefreshed: (response) => {
    store.dispatch(sessionSlice.actions.tokenRefreshed(response));
  },
  exchangeIdToken: async (idToken) => {
    const config = { headers: { "Content-Type": "application/json" } };
    const { data } = await authExchangeClient.post<AuthResponse>(
      `${getApiUrl()}/auth/firebase-login`,
      { idToken },
      config,
    );
    return data;
  },
  getCurrentFirebaseUser: async () => {
    // Native federated logins live in the NATIVE Firebase SDK (SD-16124);
    // the web session is empty in the shell by design, so resolve the
    // identity from the plugin instead. Dynamic import keeps the plugin
    // out of the web bundle.
    if (Capacitor.isNativePlatform()) {
      const { getNativeSilentReauthUser } = await import(
        "./nativeSilentReauthUser"
      );
      return getNativeSilentReauthUser();
    }
    const auth = getFirebaseAuth();
    return waitForCurrentFirebaseUser(auth.currentUser, (next) =>
      onAuthStateChanged(auth, next),
    );
  },
  getStoredSessionUser,
  isFirebaseReady,
  logger: safeConsole,
  wasIntentionalLogout,
});

export interface UnauthorizedRecoveryDependencies {
  getAccessToken: () => string | null;
  isAxiosError: typeof axios.isAxiosError;
  reauthenticate: () => Promise<boolean>;
  reportTerminalAuthFailure: typeof reportTerminalAuthFailure;
}

const unauthorizedRecoveryDependencies: UnauthorizedRecoveryDependencies = {
  getAccessToken: () => localStorage.getItem("accessToken"),
  isAxiosError: axios.isAxiosError,
  reauthenticate: silentReauth,
  reportTerminalAuthFailure,
};

/**
 * Silent-re-auth 401 recovery for an axios response-error: on a 401 from a
 * non-auth endpoint, re-mint the backend JWT once and replay the request on
 * `instance` with the fresh token; if re-auth isn't possible or the replay
 * still 401s, invoke `onAuthFailure` (clear-session-and-redirect) and reject
 * with the original error — unless a different session was stored meanwhile,
 * which this stale failure must not touch. Auth endpoints themselves are
 * excluded to avoid a re-mint loop. Non-401 errors reject untouched.
 */
export const handleUnauthorized = async (
  instance: AxiosInstance,
  cause: unknown,
  onAuthFailure: () => void,
  recovery: UnauthorizedRecoveryDependencies = unauthorizedRecoveryDependencies,
): Promise<AxiosResponse> => {
  if (!recovery.isAxiosError(cause) || cause.response?.status !== 401) {
    return Promise.reject(cause);
  }

  const originalRequest = cause.config;
  const isAuthEndpoint = originalRequest?.url?.includes("/auth/");
  let terminalOutcome: TerminalAuthRecoveryOutcome | null =
    originalRequest?._retry ? "replay_unauthorized" : null;
  // Only a request sent with the credential this path can refresh may be
  // replayed. Silent re-auth re-mints the persisted (staff) token, so replaying
  // a request that carried a DIFFERENT bearer — an impersonation session's —
  // would silently re-execute it as the staff user: the identity mixing that
  // makes a viewed-as banner lie about who acted. Such a 401 is meaningful on
  // its own (the session ended or was revoked) and must surface.
  const replayable = isReplayableAfterReauth(originalRequest);
  if (
    originalRequest &&
    !isAuthEndpoint &&
    !originalRequest._retry &&
    replayable
  ) {
    originalRequest._retry = true;
    const reauthed = await recovery.reauthenticate();
    if (reauthed) {
      const token = recovery.getAccessToken();
      if (token) {
        // Per-request header wins over the instance default, so clients
        // created with a now-stale token (createAuthenticatedClient bakes
        // the token into defaults) still replay with the fresh one.
        originalRequest.headers.Authorization = `Bearer ${token}`;
      }
      return instance(originalRequest);
    }
    terminalOutcome = "reauth_unavailable";
  }

  // Act only on the session that was refused. An invite signup, login or
  // re-mint that stored a different token while this request was out (re-auth
  // alone can take seconds) owns the session now; clearing or redirecting on
  // this stale failure would sign the user out of it. Impersonation bearers
  // never match the persisted token, so they keep their handling below.
  const currentToken = recovery.getAccessToken();
  const refusedToken = String(
    originalRequest?.headers?.Authorization ?? "",
  ).replace(/^Bearer\s+/i, "");
  if (
    replayable &&
    refusedToken &&
    currentToken &&
    refusedToken !== currentToken
  ) {
    return Promise.reject(cause);
  }

  if (!isAuthEndpoint && terminalOutcome) {
    recovery.reportTerminalAuthFailure(cause, terminalOutcome);
  }
  onAuthFailure();
  return Promise.reject(cause);
};

/** Attach `handleUnauthorized` as a response interceptor on `instance`. */
/**
 * Whether this request may be replayed after a silent re-auth.
 *
 * Silent re-auth re-mints the STAFF token, so replaying a request that carried
 * an impersonation bearer would re-execute it as the staff user — the identity
 * mixing that makes a viewed-as banner lie about who acted. Such a 401 is
 * meaningful on its own (the session ended or was revoked) and must surface.
 *
 * Keyed on the bearer having been issued for a session, not on "differs from
 * the persisted token": a client created before a refresh legitimately carries
 * a STALE staff token, and recovering those is this interceptor's main job.
 */
export const isReplayableAfterReauth = (
  request: InternalAxiosRequestConfig | undefined,
): boolean =>
  !isImpersonationAuthorization(String(request?.headers?.Authorization ?? ""));

export const attachSilentAuthRetry = (
  instance: AxiosInstance,
  onAuthFailure: () => void,
  recovery: UnauthorizedRecoveryDependencies = unauthorizedRecoveryDependencies,
): void => {
  instance.interceptors.response.use(
    (response) => response,
    (error) => handleUnauthorized(instance, error, onAuthFailure, recovery),
  );
};

/**
 * Keep the backend session alive from the persisted Firebase session, with no
 * popup: re-mint the JWT on auth-state restoration and whenever it nears expiry
 * (on an interval, so long-lived tabs don't fall off the 1-day TTL). No-op when
 * Firebase is not configured (local dev / email-password users).
 */
export const useFirebaseSilentAuth = (): void => {
  useEffect(() => {
    if (!isFirebaseReady()) {
      return;
    }
    const auth = getFirebaseAuth();

    let consecutiveFailures = 0;
    let nextAttemptAt = 0;

    const refreshIfNeeded = (user: FirebaseUser | null) => {
      // Native shell: the identity lives in the NATIVE Firebase SDK, so the
      // web-user gate would wrongly block every proactive refresh —
      // silentReauth resolves (and no-ops without) the native session itself.
      const hasIdentity = Capacitor.isNativePlatform() || user != null;
      if (
        !hasIdentity ||
        !accessTokenNeedsRefresh() ||
        Date.now() < nextAttemptAt
      ) {
        return;
      }
      void silentReauth().then((ok) => {
        if (ok) {
          consecutiveFailures = 0;
          nextAttemptAt = 0;
        } else {
          consecutiveFailures += 1;
          nextAttemptAt = Date.now() + proactiveBackoffMs(consecutiveFailures);
        }
      });
    };

    const unsubscribe = onAuthStateChanged(auth, refreshIfNeeded);
    const intervalId = window.setInterval(
      () => refreshIfNeeded(auth.currentUser),
      REFRESH_CHECK_INTERVAL_MS,
    );

    return () => {
      unsubscribe();
      window.clearInterval(intervalId);
    };
  }, []);
};
